import React, { useState, useEffect } from 'react';
import { addInfluencer, updateInfluencer, fetchInfluencers } from '../api';
import { TIERS, NICHES, LANGUAGES, DELIVERABLES, S, TIER_COLORS, tierFromFollowers } from '../constants';
import { deriveFromCities } from '../cityLookup';
import TagInput from './TagInput';

const METRIC_FIELDS = ['followers','avg_views','engagement_rate','reach','post_count_30d','video_count_30d'];
const RATE_TYPES    = ['stories','reels','shoot','static','affiliate'];

const EMPTY = {
  influencer_name:'', handle:'', tier:'Nano',
  niche:'', language:'', city:'', state:'', country:'India',
  audience:'',
  followers:'', avg_views:'', engagement_rate:'', reach:'', post_count_30d:'', video_count_30d:'',
  previous_collab:'', email_contact:'', agency_manager:'', rate:'',
  preferred_deliverables:'', brand_fit:'', past_work:'', brands:'',
  relationship_status:'', notes:'',
  rate_stories:'', rate_reels:'', rate_shoot:'', rate_static:'', rate_affiliate:'',
};

// Validation rules per field
function validate(form, activeRates) {
  const errs = {};

  if (!form.influencer_name.trim())
    errs.influencer_name = 'Influencer name is required';
  else if (form.influencer_name.trim().length < 2)
    errs.influencer_name = 'Name must be at least 2 characters';

  if (form.handle && !/^https?:\/\/.+/.test(form.handle) && !form.handle.startsWith('@'))
    errs.handle = 'Handle should be a URL (https://...) or @username';

  if (!form.niche)
    errs.niche = 'Please select at least one niche';

  if (form.email_contact && form.email_contact.includes('@')) {
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRe.test(form.email_contact))
      errs.email_contact = 'Enter a valid email address';
  }

  if (form.followers && (isNaN(form.followers) || Number(form.followers) < 0))
    errs.followers = 'Followers must be a positive number';

  if (form.avg_views && (isNaN(form.avg_views) || Number(form.avg_views) < 0))
    errs.avg_views = 'Avg views must be a positive number';

  if (form.engagement_rate) {
    const er = parseFloat(form.engagement_rate);
    if (isNaN(er) || er < 0)
      errs.engagement_rate = 'Must be a positive decimal';
    else if (er > 1)
      errs.engagement_rate = 'Enter as decimal — e.g. 0.045 not 4.5';
  }

  if (form.reach && (isNaN(form.reach) || Number(form.reach) < 0))
    errs.reach = 'Reach must be a positive number';

  if (form.post_count_30d && (isNaN(form.post_count_30d) || !Number.isInteger(Number(form.post_count_30d)) || Number(form.post_count_30d) < 0))
    errs.post_count_30d = 'Must be a whole number';

  if (form.video_count_30d && (isNaN(form.video_count_30d) || !Number.isInteger(Number(form.video_count_30d)) || Number(form.video_count_30d) < 0))
    errs.video_count_30d = 'Must be a whole number';

  if (form.rate && (isNaN(form.rate) || Number(form.rate) < 0))
    errs.rate = 'Rate must be a positive number';

  activeRates.forEach(rt => {
    const v = form[`rate_${rt}`];
    if (!v || v === '')
      errs[`rate_${rt}`] = `Enter rate for ${rt}`;
    else if (isNaN(v) || Number(v) <= 0)
      errs[`rate_${rt}`] = 'Must be a positive number';
  });

  return errs;
}

// Field wrapper — shows error above the label if present
function F({ label, error, hint, children, full, style }) {
  return (
    <div style={{ ...(full ? { gridColumn:'1 / -1' } : {}), ...style }}>
      {error && (
        <div style={{ fontSize:11, color:'#E55D4A', marginBottom:4, display:'flex', alignItems:'center', gap:4 }}>
          <span>⚠</span> {error}
        </div>
      )}
      <label style={{ ...S.lbl, color: error ? '#E55D4A' : undefined }}>
        {label}
        {hint && <span style={{ fontSize:10, color:'rgba(245,235,221,0.25)', fontWeight:400, marginLeft:6 }}>{hint}</span>}
      </label>
      {children}
    </div>
  );
}

function LockedField({ label, value, hint }) {
  const hasValue = value && value.trim();
  return (
    <div>
      <label style={S.lbl}>
        {label}{' '}
        {hasValue
          ? <span style={{ fontSize:9, color:'rgba(216,224,77,0.7)', fontWeight:400 }}>auto-derived</span>
          : <span style={{ fontSize:9, color:'rgba(245,235,221,0.2)', fontWeight:400 }}>enter city to auto-fill</span>}
      </label>
      <div style={{
        ...S.inp,
        background: hasValue ? 'rgba(216,224,77,0.08)' : 'rgba(245,235,221,0.02)',
        color: hasValue ? 'rgba(245,235,221,0.75)' : 'rgba(245,235,221,0.2)',
        borderColor: hasValue ? 'rgba(216,224,77,0.4)' : 'rgba(245,235,221,0.08)',
        borderStyle: 'dashed', cursor: 'not-allowed', fontSize: 13,
      }}>
        {hasValue ? value : hint}
      </div>
    </div>
  );
}

export default function InfluencerForm({ platform, platformLabel, editData, onSaved, onCancel }) {
  const [form, setForm]         = useState(EMPTY);
  const [loading, setLoading]   = useState(false);
  const [success, setSuccess]   = useState('');
  const [errors, setErrors]     = useState({});
  const [touched, setTouched]   = useState({});
  const [activeRates, setActiveRates] = useState([]);
  const [cityHint, setCityHint] = useState('');

  useEffect(() => {
    if (editData) {
      const f = {};
      Object.keys(EMPTY).forEach(k => { f[k] = editData[k] ?? ''; });
      setForm(f);
      setActiveRates(RATE_TYPES.filter(rt => editData[`rate_${rt}`] != null && editData[`rate_${rt}`] !== ''));
      updateCityHint(editData.city || '');
    } else {
      setForm(EMPTY);
      setActiveRates([]);
      setErrors({});
      setTouched({});
      setCityHint('');
    }
  }, [editData, platform]);

  function updateCityHint(cityStr) {
    if (!cityStr) { setCityHint(''); return; }
    const { state, country } = deriveFromCities(cityStr);
    const cities = cityStr.split(',').map(s=>s.trim()).filter(Boolean);
    if (cities.length > 0 && !state && !country)
      setCityHint('City not in lookup — enter state/country manually');
    else if (cities.length > 1 && (!state || !country))
      setCityHint('Multiple regions — state/country cleared');
    else setCityHint('');
  }

  function set(k, v) {
    setForm(prev => ({...prev, [k]: v}));
    setTouched(prev => ({...prev, [k]: true}));
    // Clear error for this field on change
    if (errors[k]) setErrors(prev => { const e={...prev}; delete e[k]; return e; });
  }

  function blur(k) { setTouched(prev => ({...prev, [k]: true})); }

  function toggleRate(rt) {
    setActiveRates(prev => prev.includes(rt) ? prev.filter(x=>x!==rt) : [...prev, rt]);
    if (errors[`rate_${rt}`]) setErrors(prev => { const e={...prev}; delete e[`rate_${rt}`]; return e; });
  }

  function handleCityChange(v) {
    const { state, country } = deriveFromCities(v);
    setForm(prev => ({
      ...prev, city: v,
      state:   state   || (v ? prev.state   : ''),
      country: country || (v ? prev.country : 'India'),
    }));
    updateCityHint(v);
    if (errors.city) setErrors(prev => { const e={...prev}; delete e.city; return e; });
  }

  const [duplicateWarning, setDuplicateWarning] = useState(null); // { name, handle }

  async function handleSubmit(e) {
    e.preventDefault();
    // Mark all fields touched
    const allTouched = {};
    Object.keys(EMPTY).forEach(k => { allTouched[k] = true; });
    RATE_TYPES.forEach(rt => { allTouched[`rate_${rt}`] = true; });
    setTouched(allTouched);

    const errs = validate(form, activeRates);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      setTimeout(() => {
        const el = document.querySelector('.field-error');
        if (el) el.scrollIntoView({ behavior:'smooth', block:'center' });
      }, 50);
      return;
    }

    // Check for duplicate handle (only on add, not edit)
    if (!editData && form.handle && form.handle.trim()) {
      try {
        const existing = await fetchInfluencers(platform, {});
        const handle = form.handle.trim().toLowerCase().replace(/\/$/, '');
        const duplicate = (existing.data || []).find(inf => {
          const infHandle = (inf.handle || '').trim().toLowerCase().replace(/\/$/, '');
          return infHandle === handle && infHandle !== '';
        });
        if (duplicate) {
          setDuplicateWarning({ name: duplicate.influencer_name, handle: duplicate.handle });
          return;
        }
      } catch(e) {
        // If check fails, proceed anyway
      }
    }

    setErrors({}); setSuccess(''); setLoading(true);
    submitForm();
  }

  async function submitForm() {
    setErrors({}); setSuccess(''); setLoading(true);
    try {
      const payload = {...form};
      ['followers','avg_views','reach','post_count_30d','video_count_30d'].forEach(k => {
        payload[k] = payload[k] !== '' ? parseInt(payload[k]) : null;
      });
      payload.engagement_rate = payload.engagement_rate !== '' ? parseFloat(payload.engagement_rate) : null;
      payload.rate = payload.rate !== '' ? parseFloat(payload.rate) || null : null;
      RATE_TYPES.forEach(k => {
        const key = `rate_${k}`;
        if (!activeRates.includes(k)) payload[key] = null;
        else payload[key] = payload[key] !== '' ? parseFloat(payload[key]) : null;
      });

      if (editData) {
        await updateInfluencer(platform, editData.id, payload);
        setSuccess('Updated successfully!');
      } else {
        await addInfluencer(platform, payload);
        setSuccess('Influencer added!');
        setForm(EMPTY); setActiveRates([]); setTouched({}); setCityHint('');
      }
      setTimeout(() => { setSuccess(''); onSaved && onSaved(); }, 1200);
    } catch(err) { setErrors({ _server: err.message }); }
    finally { setLoading(false); }
  }

  const grid = { display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 };
  const sel  = (k, opts, placeholder='Select...') => (
    <select
      style={{ ...S.inp, cursor:'pointer', background:'#2B2B1F', colorScheme:'dark', borderColor: errors[k] ? '#E55D4A' : undefined }}
      value={form[k]} onChange={e=>set(k,e.target.value)} onBlur={()=>blur(k)}
    >
      <option value="">{placeholder}</option>
      {opts.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );

  const inp = (k, props={}) => (
    <input
      {...props}
      style={{ ...S.inp, borderColor: errors[k] ? '#E55D4A' : undefined, ...props.style }}
      value={form[k]}
      onChange={e => set(k, e.target.value)}
      onBlur={() => blur(k)}
    />
  );

  const C = {
    niche: '#1D9E75', language: '#E55D4A', city: '#85B7EB',
    previous_collab: '#EF9F27', preferred_deliverables: '#ED93B1', brands: '#2C7CE5',
  };
  const derived       = deriveFromCities(form.city);
  const stateIsLocked = !!derived.state;
  const countryLocked = !!derived.country;

  return (
    <div style={{ padding:'2rem', display:'flex', justifyContent:'center', alignItems:'flex-start', minHeight:'calc(100vh - 120px)' }}>
    <div style={{ width:'100%', maxWidth:720 }}>
      <div style={{ fontSize:15, fontWeight:600, marginBottom:'1.25rem', color:'#F5EBDD' }}>
        {editData ? `Edit — ${editData.influencer_name}` : `Add to ${platformLabel}`}
      </div>

      <form onSubmit={handleSubmit} noValidate
        onKeyDown={e => { if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') e.preventDefault(); }}>

        {/* Duplicate handle warning modal */}
        {duplicateWarning && (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:'2rem' }}>
            <div style={{ background:'#1F1F1B', border:`0.5px solid rgba(229,93,74,0.4)`, borderRadius:14, padding:'2rem', maxWidth:420, width:'100%' }}>
              <div style={{ fontSize:28, marginBottom:12, textAlign:'center' }}>⚠️</div>
              <div style={{ fontSize:16, fontWeight:700, color:'#F5EBDD', marginBottom:8, textAlign:'center' }}>Duplicate Handle Found</div>
              <div style={{ fontSize:13, color:'rgba(245,235,221,0.6)', textAlign:'center', lineHeight:1.6, marginBottom:'1.5rem' }}>
                An influencer with this handle already exists:<br/>
                <strong style={{ color:'#F5EBDD' }}>{duplicateWarning.name}</strong><br/>
                <span style={{ fontSize:11, color:'rgba(245,235,221,0.4)' }}>{duplicateWarning.handle}</span>
              </div>
              <div style={{ display:'flex', gap:10 }}>
                <button type="button" onClick={() => setDuplicateWarning(null)}
                  style={{ flex:1, background:'none', border:`0.5px solid rgba(245,235,221,0.2)`, borderRadius:8, padding:'10px', fontSize:13, color:'rgba(245,235,221,0.6)', cursor:'pointer', fontFamily:'inherit' }}>
                  Cancel
                </button>
                <button type="button" onClick={() => { setDuplicateWarning(null); submitForm(); }}
                  style={{ flex:1, background:'rgba(229,93,74,0.2)', border:`0.5px solid rgba(229,93,74,0.5)`, borderRadius:8, padding:'10px', fontSize:13, color:'#E55D4A', cursor:'pointer', fontFamily:'inherit', fontWeight:600 }}>
                  Add Anyway
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Basic info ── */}
        <div style={{...S.sec, marginTop:0}}>Basic info</div>
        <div style={grid}>
          <F label="Influencer name *" error={errors.influencer_name}>
            {inp('influencer_name', { placeholder:'e.g. Priya Sharma' })}
          </F>
          <F label="Handle / Profile URL" error={errors.handle} hint="URL or @username">
            {inp('handle', { placeholder:'https://instagram.com/...' })}
          </F>
          <F label="Tier" hint="auto-calculated from followers">
            <div style={{
              ...S.inp,
              background: form.followers ? `${TIER_COLORS[tierFromFollowers(form.followers)]}18` : 'rgba(245,235,221,0.02)',
              borderColor: form.followers ? `${TIER_COLORS[tierFromFollowers(form.followers)]}60` : 'rgba(245,235,221,0.08)',
              color: form.followers ? TIER_COLORS[tierFromFollowers(form.followers)] : 'rgba(245,235,221,0.3)',
              cursor: 'not-allowed', fontWeight: 600, fontSize: 13,
            }}>
              {form.followers ? tierFromFollowers(form.followers) : '— enter followers count first —'}
            </div>
          </F>
          <F label="Relationship Status" error={errors.relationship_status}>
            {inp('relationship_status', { placeholder:'Active / Prospect / Inactive' })}
          </F>
          <F label="Niche / Category *" error={errors.niche} hint="multi-select" full>
            <TagInput value={form.niche} onChange={v=>set('niche',v)} suggestions={NICHES} placeholder="Select or type niches..." color={C.niche} />
          </F>
          <F label="Language(s)" error={errors.language} hint="multi-select" full>
            <TagInput value={form.language} onChange={v=>set('language',v)} suggestions={LANGUAGES} placeholder="Select or type languages..." color={C.language} />
          </F>
          <F label="Audience Description" error={errors.audience} full>
            {inp('audience', { placeholder:'e.g. Young moms, tier 2 cities, 25-35 age group' })}
          </F>
        </div>

        {/* ── Location ── */}
        <div style={S.sec}>Location</div>
        <div style={grid}>
          <F label="City" error={errors.city} hint="multiple cities allowed · state & country auto-fill" full>
            <TagInput value={form.city} onChange={handleCityChange} suggestions={[]} placeholder="Type a city and press Enter..." color={C.city} />
            {cityHint && <div style={{ fontSize:10, color:'rgba(239,159,39,0.7)', marginTop:4 }}>⚠ {cityHint}</div>}
          </F>
          <div>
            {stateIsLocked
              ? <LockedField label="State" value={form.state} hint="— enter city first" />
              : <F label="State" error={errors.state}>{inp('state', { placeholder:'e.g. Maharashtra' })}</F>}
          </div>
          <div>
            {countryLocked
              ? <LockedField label="Country" value={form.country} hint="— enter city first" />
              : <F label="Country" error={errors.country}>{inp('country', { placeholder:'India' })}</F>}
          </div>
        </div>

        {/* ── Metrics ── */}
        <div style={S.sec}>
          Metrics
          <span style={{ fontSize:10, fontWeight:400, color:'rgba(245,235,221,0.25)', marginLeft:8 }}>
            — enter manually or auto-populate via ↻ sync on the sheet
          </span>
        </div>
        <div style={grid}>
          <F label="Followers" error={errors.followers}>
            {inp('followers', { type:'number', placeholder:'e.g. 250000' })}
          </F>
          <F label="Avg Views" error={errors.avg_views}>
            {inp('avg_views', { type:'number', placeholder:'e.g. 50000' })}
          </F>
          <F label="Engagement Rate" error={errors.engagement_rate} hint="decimal e.g. 0.045 = 4.5%">
            {inp('engagement_rate', { type:'number', step:'0.0001', placeholder:'e.g. 0.045' })}
            {form.engagement_rate !== '' && !isNaN(form.engagement_rate) && !errors.engagement_rate && (
              <div style={{ fontSize:11, color:'#1D9E75', marginTop:4 }}>= {(parseFloat(form.engagement_rate)*100).toFixed(2)}%</div>
            )}
          </F>
          <F label="Avg Reach / Post" error={errors.reach}>
            {inp('reach', { type:'number', placeholder:'e.g. 30000' })}
          </F>
          <F label="Posts (last 30 days)" error={errors.post_count_30d}>
            {inp('post_count_30d', { type:'number', placeholder:'e.g. 12' })}
          </F>
          <F label="Videos (last 30 days)" error={errors.video_count_30d}>
            {inp('video_count_30d', { type:'number', placeholder:'e.g. 8' })}
          </F>
        </div>

        {/* ── Commercial ── */}
        <div style={S.sec}>Commercial</div>
        <div style={grid}>
          <F label="Rate (₹)" error={errors.rate} hint="overall / default rate">
            {inp('rate', { type:'number', placeholder:'e.g. 15000' })}
          </F>
          <F label="Brand Fit" error={errors.brand_fit}>
            {sel('brand_fit', ['Y','N'], 'Not assessed')}
          </F>
          <F label="Preferred Deliverables" error={errors.preferred_deliverables} hint="multi-select" full>
            <TagInput value={form.preferred_deliverables} onChange={v=>set('preferred_deliverables',v)} suggestions={DELIVERABLES} placeholder="Select deliverables..." color={C.preferred_deliverables} />
          </F>
        </div>

        {/* ── Rate breakdown ── */}
        <div style={S.sec}>
          Rate Breakdown
          <span style={{ fontSize:10, fontWeight:400, color:'rgba(245,235,221,0.3)', marginLeft:6 }}>— select applicable types</span>
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
          {RATE_TYPES.map(rt => (
            <div key={rt}>
              <div style={{
                display:'flex', alignItems:'center', gap:12,
                background: activeRates.includes(rt) ? 'rgba(239,159,39,0.07)' : 'rgba(245,235,221,0.02)',
                border: `0.5px solid ${errors[`rate_${rt}`] ? '#E55D4A' : activeRates.includes(rt) ? 'rgba(239,159,39,0.3)' : 'rgba(245,235,221,0.08)'}`,
                borderRadius:8, padding:'10px 14px', transition:'all 0.15s',
              }}>
                <input type="checkbox" id={`rate_${rt}`} checked={activeRates.includes(rt)} onChange={()=>toggleRate(rt)}
                  style={{ width:16, height:16, cursor:'pointer', accentColor:'#EF9F27' }} />
                <label htmlFor={`rate_${rt}`} style={{
                  fontSize:13, fontWeight: activeRates.includes(rt)?500:400,
                  color: activeRates.includes(rt)?'#F5EBDD':'rgba(245,235,221,0.45)',
                  cursor:'pointer', minWidth:80, textTransform:'capitalize',
                }}>{rt}</label>
                {activeRates.includes(rt) && (
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginLeft:'auto' }}>
                    <span style={{ fontSize:12, color:'rgba(245,235,221,0.4)' }}>₹</span>
                    <input
                      style={{ ...S.inp, width:160, padding:'7px 11px', fontSize:13, borderColor: errors[`rate_${rt}`] ? '#E55D4A' : undefined }}
                      type="number" value={form[`rate_${rt}`]}
                      onChange={e=>set(`rate_${rt}`,e.target.value)}
                      onBlur={()=>blur(`rate_${rt}`)}
                      placeholder="Enter rate"
                    />
                  </div>
                )}
              </div>
              {errors[`rate_${rt}`] && <div className="field-error" style={{ fontSize:11, color:'#E55D4A', marginTop:4, paddingLeft:2 }}>⚠ {errors[`rate_${rt}`]}</div>}
            </div>
          ))}
        </div>

        {/* ── Collaboration ── */}
        <div style={S.sec}>Collaboration details</div>
        <div style={grid}>
          <F label="Previous Collaboration(s)" error={errors.previous_collab} hint="multiple allowed" full>
            <TagInput value={form.previous_collab} onChange={v=>set('previous_collab',v)} suggestions={[]} placeholder="Type brand name and press Enter..." color={C.previous_collab} />
          </F>
          <F label="Brands" hint="brands this influencer has worked with" full>
            <TagInput value={form.brands} onChange={v=>set('brands',v)} suggestions={[]} placeholder="e.g. Nike, Mango, Zara..." color={C.brands} />
          </F>
          <F label="Email / Contact" error={errors.email_contact}>
            {inp('email_contact', { placeholder:'email or phone' })}
          </F>
          <F label="Agency / Manager" error={errors.agency_manager}>
            {inp('agency_manager', { placeholder:'Agency name' })}
          </F>
          <F label="Past Work" error={errors.past_work} full>
            {inp('past_work', { placeholder:'Links or description of past work' })}
          </F>
          <F label="Notes" error={errors.notes} full>
            {inp('notes', { placeholder:'Any additional notes...' })}
          </F>
        </div>

        {errors._server && <div style={{ fontSize:13, color:'#E55D4A', marginTop:12, padding:'8px 12px', background:'rgba(229,93,74,0.1)', borderRadius:6 }}>⚠ {errors._server}</div>}
        {success        && <div style={{ fontSize:13, color:'#1D9E75', marginTop:12 }}>{success}</div>}

        {Object.keys(errors).filter(k=>k!=='_server').length > 0 && (
          <div style={{ fontSize:12, color:'#E55D4A', marginTop:10, padding:'8px 12px', background:'rgba(229,93,74,0.08)', borderRadius:6, border:'0.5px solid rgba(229,93,74,0.3)' }}>
            ⚠ Please fix {Object.keys(errors).filter(k=>k!=='_server').length} error{Object.keys(errors).filter(k=>k!=='_server').length>1?'s':''} above before saving
          </div>
        )}

        <div style={{ display:'flex', gap:10, marginTop:'1.5rem' }}>
          <button type="submit" disabled={loading} style={{...S.btnPrimary, opacity:loading?0.5:1}}>
            {loading ? 'Saving...' : editData ? 'Update' : 'Save Influencer'}
          </button>
          {onCancel && <button type="button" onClick={onCancel} style={S.btnSecondary}>Cancel</button>}
        </div>
      </form>
    </div>
    </div>
  );
}
